import React, { useState, useEffect } from 'react';
import { 
    Search, Filter, Download, RefreshCw, TrendingUp, 
    Building2, Calendar, Users, AlertCircle, ChevronDown,
    Eye, FileText, BarChart3, PieChart, X
} from 'lucide-react';
import * as XLSX from 'xlsx';
import http from '../../frameworks/basic-rest/http';
import './SurveyDashboard.css';

const SurveyDashboard = () => {
    const [responses, setResponses] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    
    // Filters
    const [filters, setFilters] = useState({
        activityType: '',
        companySize: '',
        startDate: '',
        endDate: '',
        search: ''
    });
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 10;

    // Fetch responses
    const fetchResponses = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const queryParams = new URLSearchParams({
                page: currentPage,
                limit: itemsPerPage,
                ...(filters.activityType && { activityType: filters.activityType }),
                ...(filters.companySize && { companySize: filters.companySize }),
                ...(filters.startDate && { startDate: filters.startDate }),
                ...(filters.endDate && { endDate: filters.endDate })
            });

            const response = await http.get(`/survey/?${queryParams}`);
            
            setResponses(response.data.data);
            setTotalPages(response.data.pagination.totalPages);
            setTotalItems(response.data.pagination.totalItems);
        } catch (err) {
            setError('فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.');
            console.error('Error fetching responses:', err);
        } finally {
            setLoading(false);
        }
    };

    // Helper: safely get nested values
const get = (obj, path, fallback = '') =>
  path.split('.').reduce((acc, key) => (acc && acc[key] != null ? acc[key] : undefined), obj) ?? fallback;

// Optional: map one response into one Excel row (edit columns as you like)
const mapResponseToRow = (r) => ({
  id: r._id ?? '',
  companyName: get(r, 'companyInfo.name'),
  activityType: get(r, 'companyInfo.activityType'),
  activityField: get(r, 'companyInfo.activityField'),
  companySize: get(r, 'companyInfo.companySize'),
  companyAge: get(r, 'companyInfo.companyAge'),
  respondentPosition: get(r, 'companyInfo.respondentPosition'),

  dataDisclosure_importance: get(r, 'dataDisclosure.importance'),
  dataDisclosure_dataImpact: get(r, 'dataDisclosure.dataImpact'),
  dataDisclosure_cooperationLevel: get(r, 'dataDisclosure.cooperationLevel'),

  operationalBenefits: (get(r, 'dataDisclosure.operationalBenefits', []) || []).join(' | '),
  legalBenefits: (get(r, 'dataDisclosure.legalBenefits', []) || []).join(' | '),
  financialBenefits: (get(r, 'dataDisclosure.financialBenefits', []) || []).join(' | '),
  securityBenefits: (get(r, 'dataDisclosure.securityBenefits', []) || []).join(' | '),
  nonDisclosureRisks: (get(r, 'dataDisclosure.nonDisclosureRisks', []) || []).join(' | '),

  leadershipPositions: (get(r, 'leadershipPositions.positions', []) || []).filter(Boolean).join(' | '),
  leadership_financialData: (get(r, 'leadershipPositions.financialData', []) || []).join(' | '),
  leadership_legalData: (get(r, 'leadershipPositions.legalData', []) || []).join(' | '),

  specializedPositions: (get(r, 'specializedPositions.positions', []) || []).filter(Boolean).join(' | '),
  specialized_financialData: (get(r, 'specializedPositions.financialData', []) || []).join(' | '),
  specialized_academicData: (get(r, 'specializedPositions.academicData', []) || []).join(' | '),

  administrativePositions: (get(r, 'administrativePositions.positions', []) || []).filter(Boolean).join(' | '),
  administrative_academicData: (get(r, 'administrativePositions.academicData', []) || []).join(' | '),

  annualCosts: get(r, 'costsAndProcedures.annualCosts'),
  verificationCostPerCandidate: get(r, 'costsAndProcedures.verificationCostPerCandidate'),
  verificationTime: get(r, 'costsAndProcedures.verificationTime'),
  monthlyApplicants: get(r, 'costsAndProcedures.monthlyApplicants'),
  monthlyInterviews: get(r, 'costsAndProcedures.monthlyInterviews'),
  financialCosts: (get(r, 'costsAndProcedures.financialCosts', []) || []).join(' | '),
  currentProcedures: (get(r, 'costsAndProcedures.currentProcedures', []) || []).join(' | '),

  verify_shareholders: get(r, 'verification.shareholders.required') ? 'Yes' : 'No',
  verify_suppliers: get(r, 'verification.suppliers.required') ? 'Yes' : 'No',
  verify_otherParties: (get(r, 'verification.otherParties', []) || []).join(' | '),

  submittedAt: r.submittedAt ? new Date(r.submittedAt).toISOString() : '',
  completionTimeMinutes: r.completionTime ? Math.round(r.completionTime / 60) : '',
  comments: r.comments ?? '',
});

// Fetch all pages using existing /survey pagination
const fetchAllSurveyResponses = async ({ filters, itemsPerPage }) => {
  let page = 1;
  let all = [];
  let totalPagesLocal = 1;

  // Only include non-empty filters (same style as your fetchResponses)
  const buildParams = (pageNum) => {
    const params = new URLSearchParams({
      page: String(pageNum),
      limit: String(itemsPerPage),
      ...(filters.activityType && { activityType: filters.activityType }),
      ...(filters.companySize && { companySize: filters.companySize }),
      ...(filters.startDate && { startDate: filters.startDate }),
      ...(filters.endDate && { endDate: filters.endDate }),
      // NOTE: your backend currently doesn't use search in fetchResponses.
      // If it supports it, include it:
      ...(filters.search && { search: filters.search }),
    });
    return params.toString();
  };

  while (page <= totalPagesLocal) {
    const res = await http.get(`/survey/?${buildParams(page)}`);
    const data = res.data?.data ?? [];
    const pagination = res.data?.pagination ?? {};

    all = all.concat(data);
    totalPagesLocal = pagination.totalPages ?? totalPagesLocal;

    page += 1;
  }

  return all;
};

const exportData = async () => {
  try {
    setLoading(true);

    // 1) fetch everything (not just current page)
    const allResponses = await fetchAllSurveyResponses({ filters, itemsPerPage });

    // 2) map to rows
    const rows = allResponses.map(mapResponseToRow);

    // 3) build workbook
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Survey Responses');

    // 4) download
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `survey-responses-${date}.xlsx`);
  } catch (err) {
    console.error('Error exporting data:', err);
  } finally {
    setLoading(false);
  }
};

    // Fetch statistics
    // const fetchStatistics = async () => {
    //     try {
    //         const queryParams = new URLSearchParams({
    //             ...(filters.activityType && { activityType: filters.activityType }),
    //             ...(filters.companySize && { companySize: filters.companySize }),
    //             ...(filters.startDate && { startDate: filters.startDate }),
    //             ...(filters.endDate && { endDate: filters.endDate })
    //         });

    //         const response = await http.get(`/survey/statistics?${queryParams}`);
    //         setStatistics(response.data.data);
    //     } catch (err) {
    //         console.error('Error fetching statistics:', err);
    //     }
    // };

    useEffect(() => {
        fetchResponses();
       // fetchStatistics();
    }, [currentPage, filters]);

    // Handle filter changes
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1); // Reset to first page when filtering
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            activityType: '',
            companySize: '',
            startDate: '',
            endDate: '',
            search: ''
        });
        setCurrentPage(1);
    };

    // View response details
    const viewDetails = async (id) => {
        try {
            const response = await http.get(`/survey/${id}`);
            setSelectedResponse(response.data.data);
            setShowDetailModal(true);
        } catch (err) {
            console.error('Error fetching response details:', err);
        }
    };

    // Export data
    // const exportData = async () => {
    //     try {
    //         const queryParams = new URLSearchParams(filters);
    //         const response = await http.get(`/survey-responses/report?${queryParams}`);
            
    //         // Convert to CSV or download
    //         const blob = new Blob([JSON.stringify(response.data, null, 2)], { 
    //             type: 'application/json' 
    //         });
    //         const url = window.URL.createObjectURL(blob);
    //         const a = document.createElement('a');
    //         a.href = url;
    //         a.download = `survey-responses-${new Date().toISOString()}.json`;
    //         a.click();
    //     } catch (err) {
    //         console.error('Error exporting data:', err);
    //     }
    // };

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('ar-SA', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Statistics cards
    const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
        <div className="stat-card" style={{ borderTopColor: color }}>
            <div className="stat-icon" style={{ backgroundColor: `${color}15`, color }}>
                <Icon size={24} />
            </div>
            <div className="stat-content">
                <h3>{title}</h3>
                <p className="stat-value">{value}</p>
                {subtitle && <p className="stat-subtitle">{subtitle}</p>}
            </div>
        </div>
    );

    return (
        <div className="dashboard-container">
            {/* Header */}
            <div className="dashboard-header">
                <div className="header-content">
                    <h1>لوحة تحكم الاستبيانات</h1>
                    <p>إدارة ومراجعة ردود الاستبيانات</p>
                </div>
                <div className="header-actions">
                    <button className="btn-icon" onClick={fetchResponses} title="تحديث">
                        <RefreshCw size={20} />
                    </button>
                    <button className="btn-primary" onClick={exportData}>
                        <Download size={20} />
                        تصدير البيانات
                    </button>
                </div>
            </div>

            {/* Statistics */}
            {statistics && (
                <div className="stats-grid">
                    <StatCard
                        icon={FileText}
                        title="إجمالي الردود"
                        value={statistics.totalResponses || 0}
                        color="#3b82f6"
                        subtitle="جميع الاستبيانات"
                    />
                    <StatCard
                        icon={Building2}
                        title="عدد الشركات"
                        value={statistics.byIndustry?.length || 0}
                        color="#10b981"
                        subtitle="قطاعات مختلفة"
                    />
                    <StatCard
                        icon={TrendingUp}
                        title="معدل الإكمال"
                        value="95%"
                        color="#f59e0b"
                        subtitle="نسبة الإكمال"
                    />
                    <StatCard
                        icon={Calendar}
                        title="متوسط الوقت"
                        value={`${Math.round((statistics.averageCompletionTime?.[0]?.avg || 0) / 60)} دقيقة`}
                        color="#8b5cf6"
                        subtitle="وقت الإكمال"
                    />
                </div>
            )}

            {/* Filters */}
            {/* <div className="filters-section">
                <div className="filters-header">
                    <Filter size={20} />
                    <h3>تصفية النتائج</h3>
                </div>
                
                <div className="filters-grid">
                    <div className="filter-group">
                        <label>نوع النشاط</label>
                        <select
                            value={filters.activityType}
                            onChange={(e) => handleFilterChange('activityType', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">الكل</option>
                            <option value="صناعي">صناعي</option>
                            <option value="زراعي">زراعي</option>
                            <option value="تجاري">تجاري</option>
                            <option value="خدمي">خدمي</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>حجم الشركة</label>
                        <select
                            value={filters.companySize}
                            onChange={(e) => handleFilterChange('companySize', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">الكل</option>
                            <option value="صغيرة (أقل من 50 موظف)">صغيرة</option>
                            <option value="متوسطة (51-250 موظف)">متوسطة</option>
                            <option value="كبيرة (251-1000 موظف)">كبيرة</option>
                            <option value="كبيرة جداً (أكثر من 1001 موظف)">كبيرة جداً</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>من تاريخ</label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            className="filter-input"
                        />
                    </div>

                    <div className="filter-group">
                        <label>إلى تاريخ</label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            className="filter-input"
                        />
                    </div>

                    <div className="filter-group filter-actions">
                        <button 
                            className="btn-clear-filters" 
                            onClick={clearFilters}
                        >
                            مسح الفلاتر
                        </button>
                    </div>
                </div>
            </div> */}

            {/* Results Table */}
            <div className="results-section">
                <div className="results-header">
                    <h3>النتائج ({totalItems})</h3>
                    <div className="search-box">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="بحث..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <RefreshCw className="spin" size={40} />
                        <p>جاري التحميل...</p>
                    </div>
                ) : error ? (
                    <div className="error-state">
                        <AlertCircle size={40} />
                        <p>{error}</p>
                        <button className="btn-retry" onClick={fetchResponses}>
                            إعادة المحاولة
                        </button>
                    </div>
                ) : responses.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <h3>لا توجد نتائج</h3>
                        <p>لم يتم العثور على استبيانات تطابق معايير البحث</p>
                    </div>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="responses-table">
                                <thead>
                                    <tr>
                                        <th>اسم الشركة</th>
                                        <th>نوع النشاط</th>
                                        <th>حجم الشركة</th>
                                        <th>المنصب</th>
                                        <th>تاريخ الإرسال</th>
                                        <th>الإجراءات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {responses.map((response) => (
                                        <tr key={response._id}>
                                            <td>
                                                <div className="company-cell">
                                                    <Building2 size={16} />
                                                    <span>{response.companyInfo?.name || 'غير محدد'}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="badge-type badge-blue">
                                                    {response.companyInfo?.activityType}
                                                </span>
                                            </td>
                                            <td>{response.companyInfo?.companySize}</td>
                                            <td className="position-cell">
                                                {response.companyInfo?.respondentPosition}
                                            </td>
                                            <td>
                                                <div className="date-cell">
                                                    <Calendar size={14} />
                                                    {formatDate(response.submittedAt)}
                                                </div>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn-view"
                                                    onClick={() => viewDetails(response._id)}
                                                    title="عرض التفاصيل"
                                                >
                                                    <Eye size={16} />
                                                    عرض
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="pagination">
                            <button
                                className="btn-page"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                السابق
                            </button>
                            
                            <div className="page-info">
                                صفحة {currentPage} من {totalPages}
                            </div>
                            
                            <button
                                className="btn-page"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                التالي
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Detail Modal */}
            {showDetailModal && selectedResponse && (
                <DetailModal
                    response={selectedResponse}
                    onClose={() => setShowDetailModal(false)}
                />
            )}
        </div>
    );
};

// Detail Modal Component
const DetailModal = ({ response, onClose }) => {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-detail" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>تفاصيل الاستبيان</h2>
                    <button className="btn-close" onClick={onClose}>
                        <X size={24} />
                    </button>
                </div>

                <div className="modal-body">
                    {/* Company Info */}
                    <section className="detail-section">
                        <h3>📋 معلومات الشركة</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>اسم الشركة</label>
                                <p>{response.companyInfo?.name || 'غير محدد'}</p>
                            </div>
                            <div className="detail-item">
                                <label>نوع النشاط</label>
                                <p>{response.companyInfo?.activityType}</p>
                            </div>
                            <div className="detail-item">
                                <label>مجال النشاط</label>
                                <p>{response.companyInfo?.activityField}</p>
                            </div>
                            <div className="detail-item">
                                <label>حجم الشركة</label>
                                <p>{response.companyInfo?.companySize}</p>
                            </div>
                            <div className="detail-item">
                                <label>عمر الشركة</label>
                                <p>{response.companyInfo?.companyAge}</p>
                            </div>
                            <div className="detail-item">
                                <label>المنصب الوظيفي</label>
                                <p>{response.companyInfo?.respondentPosition}</p>
                            </div>
                        </div>
                    </section>

                    {/* Data Disclosure */}
                    <section className="detail-section">
                        <h3>📊 أهمية الإفصاح عن البيانات</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>مستوى الأهمية</label>
                                <p className="highlight">{response.dataDisclosure?.importance}</p>
                            </div>
                            <div className="detail-item">
                                <label>تأثير البيانات</label>
                                <p>{response.dataDisclosure?.dataImpact}</p>
                            </div>
                            <div className="detail-item">
                                <label>مستوى التعاون</label>
                                <p>{response.dataDisclosure?.cooperationLevel}</p>
                            </div>
                        </div>

                        {response.dataDisclosure?.operationalBenefits?.length > 0 && (
                            <div className="detail-item full-width">
                                <label>المزايا التشغيلية ({response.dataDisclosure.operationalBenefits.length})</label>
                                <ul className="benefits-list">
                                    {response.dataDisclosure.operationalBenefits.map((benefit, idx) => (
                                        <li key={idx}>{benefit}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {response.dataDisclosure?.legalBenefits?.length > 0 && (
                            <div className="detail-item full-width">
                                <label>المزايا القانونية ({response.dataDisclosure.legalBenefits.length})</label>
                                <ul className="benefits-list">
                                    {response.dataDisclosure.legalBenefits.map((benefit, idx) => (
                                        <li key={idx}>{benefit}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {response.dataDisclosure?.financialBenefits?.length > 0 && (
                            <div className="detail-item full-width">
                                <label>المزايا المالية ({response.dataDisclosure.financialBenefits.length})</label>
                                <ul className="benefits-list">
                                    {response.dataDisclosure.financialBenefits.map((benefit, idx) => (
                                        <li key={idx}>{benefit}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {response.dataDisclosure?.securityBenefits?.length > 0 && (
                            <div className="detail-item full-width">
                                <label>المزايا الأمنية ({response.dataDisclosure.securityBenefits.length})</label>
                                <ul className="benefits-list">
                                    {response.dataDisclosure.securityBenefits.map((benefit, idx) => (
                                        <li key={idx}>{benefit}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {response.dataDisclosure?.nonDisclosureRisks?.length > 0 && (
                            <div className="detail-item full-width">
                                <label>مخاطر عدم الإفصاح ({response.dataDisclosure.nonDisclosureRisks.length})</label>
                                <ul className="benefits-list">
                                    {response.dataDisclosure.nonDisclosureRisks.map((risk, idx) => (
                                        <li key={idx}>{risk}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>

                    {/* Leadership Positions */}
                    {response.leadershipPositions?.positions?.filter(p => p).length > 0 && (
                        <section className="detail-section">
                            <h3>👔 الوظائف القيادية</h3>
                            <div className="detail-item full-width">
                                <label>الوظائف</label>
                                <p>{response.leadershipPositions.positions.filter(p => p).join(', ')}</p>
                            </div>
                            {response.leadershipPositions?.financialData?.length > 0 && (
                                <div className="detail-item full-width">
                                    <label>البيانات المالية المطلوبة</label>
                                    <ul className="benefits-list">
                                        {response.leadershipPositions.financialData.map((data, idx) => (
                                            <li key={idx}>{data}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {response.leadershipPositions?.legalData?.length > 0 && (
                                <div className="detail-item full-width">
                                    <label>البيانات القانونية المطلوبة</label>
                                    <ul className="benefits-list">
                                        {response.leadershipPositions.legalData.map((data, idx) => (
                                            <li key={idx}>{data}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </section>
                    )}

                    {/* Specialized Positions */}
                    {response.specializedPositions?.positions?.filter(p => p).length > 0 && (
                        <section className="detail-section">
                            <h3>🎓 الوظائف التخصصية</h3>
                            <div className="detail-item full-width">
                                <label>الوظائف</label>
                                <p>{response.specializedPositions.positions.filter(p => p).join(', ')}</p>
                            </div>
                            {response.specializedPositions?.financialData?.length > 0 && (
                                <div className="detail-item full-width">
                                    <label>البيانات المالية</label>
                                    <ul className="benefits-list">
                                        {response.specializedPositions.financialData.map((data, idx) => (
                                            <li key={idx}>{data}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {response.specializedPositions?.academicData?.length > 0 && (
                                <div className="detail-item full-width">
                                    <label>البيانات الأكاديمية</label>
                                    <ul className="benefits-list">
                                        {response.specializedPositions.academicData.map((data, idx) => (
                                            <li key={idx}>{data}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </section>
                    )}

                    {/* Administrative Positions */}
                    {response.administrativePositions?.positions?.filter(p => p).length > 0 && (
                        <section className="detail-section">
                            <h3>💼 الوظائف الإدارية</h3>
                            <div className="detail-item full-width">
                                <label>الوظائف</label>
                                <p>{response.administrativePositions.positions.filter(p => p).join(', ')}</p>
                            </div>
                            {response.administrativePositions?.academicData?.length > 0 && (
                                <div className="detail-item full-width">
                                    <label>البيانات الأكاديمية والمهنية</label>
                                    <ul className="benefits-list">
                                        {response.administrativePositions.academicData.map((data, idx) => (
                                            <li key={idx}>{data}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </section>
                    )}

                    {/* Costs */}
                    <section className="detail-section">
                        <h3>💰 التكاليف والإجراءات</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>التكاليف السنوية</label>
                                <p className="highlight">{response.costsAndProcedures?.annualCosts}</p>
                            </div>
                            <div className="detail-item">
                                <label>تكلفة التحقق لكل مرشح</label>
                                <p>{response.costsAndProcedures?.verificationCostPerCandidate}</p>
                            </div>
                            <div className="detail-item">
                                <label>وقت التحقق</label>
                                <p>{response.costsAndProcedures?.verificationTime}</p>
                            </div>
                            <div className="detail-item">
                                <label>عدد المتقدمين شهرياً</label>
                                <p>{response.costsAndProcedures?.monthlyApplicants}</p>
                            </div>
                            <div className="detail-item">
                                <label>عدد المقابلات شهرياً</label>
                                <p>{response.costsAndProcedures?.monthlyInterviews}</p>
                            </div>
                        </div>

                        {response.costsAndProcedures?.financialCosts?.length > 0 && (
                            <div className="detail-item full-width">
                                <label>التكاليف المالية</label>
                                <ul className="benefits-list">
                                    {response.costsAndProcedures.financialCosts.map((cost, idx) => (
                                        <li key={idx}>{cost}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {response.costsAndProcedures?.currentProcedures?.length > 0 && (
                            <div className="detail-item full-width">
                                <label>الإجراءات الحالية</label>
                                <ul className="benefits-list">
                                    {response.costsAndProcedures.currentProcedures.map((proc, idx) => (
                                        <li key={idx}>{proc}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>

                    {/* Verification */}
                    <section className="detail-section">
                        <h3>✅ التحقق من الأطراف الأخرى</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>التحقق من المساهمين</label>
                                <p>{response.verification?.shareholders?.required ? 'نعم' : 'لا'}</p>
                            </div>
                            <div className="detail-item">
                                <label>التحقق من الموردين</label>
                                <p>{response.verification?.suppliers?.required ? 'نعم' : 'لا'}</p>
                            </div>
                        </div>

                        {response.verification?.otherParties?.length > 0 && (
                            <div className="detail-item full-width">
                                <label>أطراف أخرى تتطلب التحقق</label>
                                <ul className="benefits-list">
                                    {response.verification.otherParties.map((party, idx) => (
                                        <li key={idx}>{party}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>

                    {/* Metadata */}
                    <section className="detail-section">
                        <h3>📌 معلومات إضافية</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>تاريخ الإرسال</label>
                                <p>{new Date(response.submittedAt).toLocaleString('ar-SA')}</p>
                            </div>
                            <div className="detail-item">
                                <label>وقت الإكمال</label>
                                <p>{Math.round(response.completionTime / 60)} دقيقة</p>
                            </div>
                            {/* <div className="detail-item">
                                <label>إجمالي أنواع البيانات المطلوبة</label>
                                <p>{response.analytics?.totalDataTypesRequested || 0}</p>
                            </div> */}
                        </div>
                    </section>

                    {/* Comments */}
                    {response.comments && (
                        <section className="detail-section">
                            <h3>💬 الملاحظات والتوصيات</h3>
                            <p className="comments-text">{response.comments}</p>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SurveyDashboard;